/**
 * isiZulu: Inbox
 *
 * What this file covers: Conversations with clients on every channel: replying, notes, assigning, taking over
 * from the chatbot, canned replies.
 *
 * Tone: Quick and practical, for people answering clients all day. Short labels. When something cannot be sent,
 * say why and what to do, in one sentence.
 *
 * isiZulu: Everyday isiZulu as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const inbox: Catalog = {
  'inbox.title': 'Ibhokisi lokungenayo',
  'inbox.empty': 'Azikho izingxoxo okwamanje: namathisela inombolo bese uvumela umamukeli akuthumelele umlayezo.',
  'inbox.select': 'Khetha ingxoxo ukuze ufunde umlando futhi uphendule.',
  'inbox.noMessages': 'Ayikho imilayezo okwamanje',
  'inbox.loadingMessages': 'Ilayisha imilayezo…',
  'inbox.assign': 'Yabela i-ejenti',
  'inbox.unassigned': 'Akwabelwanga',
  'inbox.assigned': 'Ukwabiwa kubuyekeziwe.',
  'inbox.lock': 'Khiya',
  'inbox.locked': 'Ingxoxo ikhiyelwe wena.',
  'inbox.lockedBy': 'Ikhiywe ngu',
  'inbox.anotherAgent': 'enye i-ejenti',
  'inbox.send': 'Thumela',
  'inbox.newline': 'ukuze uqale umugqa omusha',
  'inbox.canned.noMatch': 'Azikho izimpendulo ezilungiselelwe ezifanayo.',
  'inbox.canned.manage': 'Phatha izimpendulo ezilungiselelwe',
  'inbox.canned.navigate': 'zulazula',
  'inbox.canned.insert': 'faka',
  'inbox.canned.close': 'vala',
  'inbox.tags.title': 'Amalebula',
  'inbox.tags.add': 'Engeza ilebula',
  'inbox.tags.placeholder': 'Igama lelebula…',
  'inbox.tags.suggestions': 'Amalebula akhona',
  'inbox.transfer': 'Dlulisa',
  'inbox.transfer.title': 'Dlulisa ingxoxo',
  'inbox.transfer.agent': 'Dlulisela ku',
  'inbox.transfer.note': 'Inothi le-ejenti eyamukelayo (ngokuzikhethela)',
  'inbox.transfer.noteprefix': 'Kudluliselwe ku',
  'inbox.transfer.keeps': 'Amanothi namalebula ahlala nengxoxo.',
  'inbox.transfer.done': 'Ingxoxo idluliselwe.',
  'inbox.sla.firstReply': 'Impendulo yokuqala',
  'inbox.sla.resolve': 'Xazulula',
  'inbox.sla.left': 'esisele',
  'inbox.sla.over': 'sedlulile',
  'inbox.sla.breached': 'I-SLA yephuliwe',
  'inbox.sla.met': 'I-SLA ifeziwe',
  'inbox.sla.due': 'Kufanele ngo',
  'inbox.notes.title': 'Amanothi angaphakathi',
  'inbox.notes.empty': 'Awekho amanothi okwamanje.',
  'inbox.notes.agent': 'I-ejenti',
  'inbox.notes.add': 'Engeza inothi',
  'inbox.lock.heldBy': 'Ikhiywe ngu',
  'inbox.lock.until': 'kuze kube',
  'inbox.lock.mine': 'Uphethe le ngxoxo',
  'inbox.lock.release': 'Dedela',
  'inbox.lock.released': 'Isikhiya sidedelwe.',
  'inbox.lock.readOnly': 'Ukufunda kuphela ngenkathi enye i-ejenti iphendula.',
  'inbox.resolve': 'Xazulula',
  'inbox.reopen': 'Vula kabusha',
  'inbox.stateChanged': 'Ingxoxo ibuyekeziwe.',
  'inbox.state.pending': 'Kulindile',
  'inbox.state.open': 'Kuvuliwe',
  'inbox.state.resolved': 'Kuxazululiwe',
  'inbox.state.archived': 'Kugciniwe',
  'canned.title': 'Izimpendulo ezilungiselelwe',
  'canned.new': 'Impendulo entsha',
  'canned.edit': 'Hlela impendulo',
  'canned.shortcut': 'Isinqamuleli',
  'canned.titleField': 'Isihloko',
  'canned.body': 'Umlayezo',
  'canned.language': 'Ulimi',
  'canned.empty': 'Azikho izimpendulo ezilungiselelwe okwamanje.',
  'canned.saved': 'Impendulo elungiselelwe ilondoloziwe.',
  'canned.deleted': 'Impendulo elungiselelwe isusiwe.',
  'canned.updated': 'Kubuyekeziwe',
  'inbox.assignFailed': 'Ukwabela kwehlulekile',
  'inbox.lockFailed': 'Ukukhiya kwehlulekile',
  'inbox.notes.addFailed': 'Ayikwazanga ukwengeza inothi.',
  'inbox.releaseFailed': 'Ukudedela kwehlulekile',
  'inbox.sendFailed': 'Ukuthumela kwehlulekile',
  'inbox.tags.addFailed': 'Ayikwazanga ukwengeza ilebula.',
  'inbox.tags.removeFailed': 'Ayikwazanga ukususa ilebula.',
  'inbox.transfer.transferFailed': 'Ukudlulisa kwehlulekile',
  'inbox.updateFailed': 'Ukubuyekeza kwehlulekile',
  'inbox.conversations': 'Izingxoxo',
  'inbox.filters.more': 'Ezinye izihlungi',
  'inbox.queue.open': 'Kuvuliwe',
  'inbox.queue.resolved': 'Kuxazululiwe',
  'inbox.showingOf': 'Kukhonjiswa {{shown}} kwangu-{{total}}',
  'inbox.sla': 'SLA',
  'inbox.tags': 'Amathegi',
  'inbox.assignedTo.label': 'Kwabelwe',
  'inbox.composer.emoji': 'Faka i-emoji',
  'inbox.composer.canned': 'Faka impendulo elungiselelwe',
  'inbox.composer.hint': 'Cindezela u-Enter ukuze uthumele, Shift+Enter umugqa omusha',
  'inbox.message.sent': 'Kuthunyelwe',
  'inbox.notes.private': 'Okuyimfihlo',
  'inbox.quickReplies.title': 'Izimpendulo ezisheshayo',
  'inbox.quickReplies.search': 'Sesha izimpendulo',
  'inbox.quickReplies.empty': 'Azikho izimpendulo ezilungiselelwe okwamanje.',
  'inbox.tip': 'Ithiphu: thayipha u-/ ebhokisini lempendulo ukuze uthole izimpendulo ezilungiselelwe, bese ucindezela u-Enter ukuthumela.',
  'inbox.sla.onTrack': 'Kusemgudwini',
  'inbox.sla.dueSoon': 'Sekuseduze',
  'inbox.replyWindow': 'Iwindi lokuphendula',
  'inbox.newMessage': 'Umyalezo omusha',
  'inbox.message.failed': 'Ayilethwanga',
  'inbox.message.failed.code': 'Ayilethwanga (ikhodi {{code}})',
  'inbox.message.failed.WINDOW_CLOSED': 'Ayilethwanga: abakubhalelanga emahoreni angu-24 adlule, ngakho lokhu bekudinga isifanekiso esamukelwe.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE': 'Ayilethwanga: i-WhatsApp iyenqabile ngenxa yenkinga yokukhokha ngakithi, hhayi ngakuwe. Siyayilungisa.',
  'inbox.message.failed.UNDELIVERABLE': 'Ayilethwanga: i-WhatsApp ayikwazanga ukuyiletha kuleyo nombolo.',
  'inbox.message.failed.NUMBER_NOT_REGISTERED': 'Ayilethwanga: inombolo othumele ngayo ayibhalisiwe ku-WhatsApp.',
  'inbox.message.failed.MEDIA_NOT_FETCHED': 'Ayilethwanga: i-WhatsApp ayikwazanga ukulanda ifayela elinamathiselwe.',
  'inbox.message.failed.META_REFUSED': 'Ayilethwanga: i-WhatsApp iyamukele bese ingayilethi.',
  'inbox.message.failed.notCharged': 'Awukhokhiswanga ngalokhu.',
  'inbox.message.template.marker': 'Isifanekiso · {{name}}',
  'inbox.message.template.noWording': 'Amagama ale mibhalo yesifanekiso awazange abhalwe phansi ngesikhathi ithunyelwa.',
  'inbox.message.template.noWording.named': 'Amagama e-“{{name}}” awazange abhalwe phansi ngesikhathi lokhu kuphuma.',
  'inbox.message.template.buttons': 'Izinkinobho kulo mlayezo',
  'inbox.message.template.header.IMAGE': 'Isithombe phezulu',
  'inbox.message.template.header.VIDEO': 'Ividiyo phezulu',
  'inbox.message.template.header.DOCUMENT': 'Ifayela phezulu',
  'inbox.message.template.header.LOCATION': 'Indawo phezulu',
  'inbox.message.failed.WINDOW_CLOSED.next': 'Kunalokho thumela isifanekiso esamukelwe. Uma sebephendulile ungabhala ngokukhululeka futhi amanye amahora angu-24.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE.next': 'Awudingi ukwenza lutho ku-Meta. Imiyalezo izoqala ukudlula futhi uma sesiyilungisile.',
  'inbox.message.failed.UNDELIVERABLE.next': 'Hlola ukuthi inombolo ilungile nokuthi isebenzisa i-WhatsApp. Uma ilungile, umuntu angabe evimbe imilayezo evela kubhizinisi lakho.',
  'inbox.message.failed.NUMBER_NOT_REGISTERED.next': 'Vula Izinombolo bese uxhuma kabusha le nombolo. Uma icela ikhodi, sizokuhola ekuqinisekiseni.',
  'inbox.message.failed.MEDIA_NOT_FETCHED.next': 'Ithumele futhi ngefayela elilayishwe lapha esikhundleni sokuxhunywa, noma uhlole ukuthi isixhumanisi sivulekile futhi sisasebenza.',
  'inbox.message.failed.META_REFUSED.next': 'Zama futhi emizuzwini embalwa. Uma kuqhubeka kwenzeka, sitshele futhi sizobheka.',
  'inbox.details.title': 'Imininingwane yomlayezo',
  'inbox.details.open': 'Kwenzekeni kulo mlayezo',
  'inbox.details.close': 'Vala imininingwane yomlayezo',
  'inbox.details.error': 'Imininingwane yalo mlayezo ayikwazanga ukulayishwa.',
  'inbox.details.timeline': 'Okwenzekile',
  'inbox.details.timeline.sent': 'Kuthunyelwe',
  'inbox.details.timeline.received': 'Kutholiwe',
  'inbox.details.timeline.delivered': 'Kulethiwe',
  'inbox.details.timeline.read': 'Kufundiwe',
  'inbox.details.timeline.failed': 'Ayilethwanga',
  'inbox.details.timeline.later': '{{duration}} kamuva',
  'inbox.details.read.absent': 'Ayikho isiqinisekiso sokufunda. I-WhatsApp ithumela esisodwa kuphela lapho umamukeli evule iziqinisekiso zokufunda, ngakho lokhu kungahlala kungenalutho noma sebewufundile umlayezo wakho.',
  'inbox.details.delivered.pending': 'I-WhatsApp ayikaqinisekisi ukulethwa okwamanje.',
  'inbox.details.why': 'Kungani ingafikanga',
  'inbox.details.why.metaCode': 'Ikhodi ye-WhatsApp',
  'inbox.details.why.metaWords': 'I-WhatsApp ithe',
  'inbox.details.cost': 'Kubize malini',
  'inbox.details.cost.unknown': 'Asikwazanga ukufunda incwadi yamabhuku yalo mlayezo, ngakho asikwazi ukusho ukuthi ubize malini.',
  'inbox.details.cost.rate': 'Inani',
  'inbox.details.cost.worked': 'Kubalwe njenge',
  'inbox.details.cost.workedValue': '{{units}} × {{rate}}',
  'inbox.details.cost.chargedAt': 'Kukhokhisiwe',
  'inbox.details.cost.refunded': 'Kubuyiselwe u-{{amount}} ngoba i-WhatsApp ayiwulethanga lo mlayezo.',
  'inbox.details.sent': 'Okuthunyelwe',
  'inbox.details.template': 'Isifanekiso',
  'inbox.details.language': 'Ulimi',
  'inbox.details.category': 'Isigaba',
  'inbox.details.wording': 'Amagama',
  'inbox.details.wording.recovered': 'Kutholakale kusuka esifanekisweni njengoba simi manje, ngakho kungahluka kokuthunyelwe.',
  'inbox.details.wording.lost': 'Akubhalwanga phansi ngesikhathi kuthunyelwa, futhi isifanekiso asisekho.',
  'inbox.details.params': 'Amagugu afakiwe',
  'inbox.details.param.header': 'Isihloko',
  'inbox.details.param.body': 'Umlayezo',
  'inbox.details.param.button': 'Inkinobho',
  'inbox.details.file.name': 'Igama lefayela',
  'inbox.details.file.type': 'Uhlobo lwefayela',
  'inbox.details.file.size': 'Usayizi wefayela',
  'inbox.details.where': 'Lapho iye khona',
  'inbox.details.whereFrom': 'Lapho ivela khona',
  'inbox.details.to': 'Kuya ku',
  'inbox.details.from': 'Kusuka ku',
  'inbox.details.sentBy': 'Ithunyelwe ngu',
  'inbox.details.sentBy.you': 'Wena',
  'inbox.details.sentBy.teammate': 'Ilungu leqembu',
  'inbox.details.sentBy.bot': 'I-chatbot yakho',
  'inbox.details.sentBy.broadcast': 'Ukusakaza',
  'inbox.details.sentBy.platform': 'RepletoryReach',
  'inbox.details.support': 'Ukuze uthole usizo',
  'inbox.details.metaId.hint': 'Caphuna lokhu uma ubuza i-WhatsApp ngalo mlayezo.',
  'inbox.details.copy': 'Kopisha',
  'inbox.details.copied': 'Kukopishiwe',
  'inbox.channel.whatsapp': 'WhatsApp',
  'inbox.channel.messenger': 'Messenger',
  'inbox.channel.instagram': 'Instagram',
  'inbox.window.closed.body.messenger': 'I-Messenger ilethela impendulo kuphela phakathi kwamahora angu-24 emva komyalezo wokugcina womuntu, futhi leso sikhathi sesidlulile. Impendulo yakho izobafinyelela uma bebhala futhi.',
  'inbox.window.closed.body.instagram': 'I-Instagram ilethela impendulo kuphela phakathi kwamahora angu-24 emva komyalezo wokugcina womuntu, futhi leso sikhathi sesidlulile. Impendulo yakho izobafinyelela uma bebhala futhi.',
  'inbox.send.notMetered': 'Umlayezo uthunyelwe, kodwa asikwazanga ukurekhoda ukuthi ubize malini. Abasekeli sebaziswe; akukho okukhokhiswe kabili.',
  'inbox.sidebar.title': 'Izilungiselelo zengxoxo',
  'inbox.sidebar.toggle': 'Izilungiselelo',
  'inbox.sidebar.close': 'Vala izilungiselelo zengxoxo',
  'inbox.sidebar.details': 'Imininingwane',
  'inbox.sidebar.shortcuts': 'Izinqamuleli zekhibhodi',
  'inbox.notes.visibility': 'Kubonwa ithimba lakho kuphela',
  'inbox.lock.hint': 'Khiya le ngxoxo ukuze kungabikho omunye ophendulayo ngesikhathi wena uphendula',
  'inbox.shortcuts.send': 'Thumela impendulo',
  'inbox.shortcuts.newline': 'Qala umugqa omusha',
  'inbox.shortcuts.canned': 'Vula izimpendulo ezilungiselelwe',
  'inbox.shortcuts.pick': 'Hamba ezimpendulweni ezilungiselelwe',
  'inbox.shortcuts.insert': 'Faka impendulo ekhethiwe',
  'inbox.shortcuts.dismiss': 'Vala uhlu lwezimpendulo ezilungiselelwe',
  'inbox.shortcuts.key.enter': 'Enter',
  'inbox.shortcuts.key.shiftEnter': 'Shift+Enter',
  'inbox.shortcuts.key.slash': '/',
  'inbox.shortcuts.key.arrows': '↑ ↓',
  'inbox.shortcuts.key.enterTab': 'Enter noma Tab',
  'inbox.shortcuts.key.esc': 'Esc',
  'inbox.bot.label': 'I-bot',
  'inbox.bot.paused': 'I-bot imisiwe',
  'inbox.bot.active': 'I-bot iyaphendula',
  'inbox.bot.resumesAt': 'I-bot iphinde iphendule kusukela ngo-{{time}}, ngaphandle uma ithimba liphendula ngaphambi kwalokho.',
  'inbox.bot.untilResolved': 'I-bot ihlala ithule kuze kube yilapho le ngxoxo ixazululiwe noma uyiqhubekisa.',
  'inbox.bot.activeHint': 'I-bot iphendula imilayezo emisha. Uma uziphendulela, iyimisa amahora angu-24.',
  'inbox.bot.resume': 'Qhubekisa i-bot',
  'inbox.bot.pause': 'Misa i-bot',
  'inbox.bot.resumed': 'I-bot izophinde iphendule kule ngxoxo.',
  'inbox.bot.pausedToast': 'I-bot imisiwe kule ngxoxo.',
  'inbox.bot.failed': 'I-bot ayikwazanga ukushintshwa kule ngxoxo.',
  'inbox.block.label': 'Ikhasimende',
  'inbox.block.blocked': 'Livinjiwe',
  'inbox.block.notBlocked': 'Alivinjwanga',
  'inbox.block.action': 'Vimba ikhasimende',
  'inbox.block.unblock': 'Susa ukuvinjwa',
  'inbox.block.title': 'Vimba leli khasimende?',
  'inbox.block.body': 'I-bot ngeke ilithumelele impendulo futhi akekho ethimbeni lakho ongalibhalela kuze kube yilapho ususa ukuvinjwa. Imilayezo yalo isagcinwa lapha.',
  'inbox.block.reason': 'Isizathu',
  'inbox.block.reasonHint': 'Kwethimba lakho. Ikhasimende alisiboni.',
  'inbox.block.alsoWhatsapp': 'Vimba nale nombolo ku-WhatsApp',
  'inbox.block.alsoWhatsappHint': 'Ngeke bakwazi ukuthumela umlayezo enombolweni yakho nhlobo. I-WhatsApp ivimba kuphela umuntu obhale emahoreni angu-24 edlule.',
  'inbox.block.confirm': 'Vimba',
  'inbox.block.done': 'Ikhasimende livinjiwe.',
  'inbox.block.doneWhatsapp': 'Ikhasimende livinjiwe lapha naku-WhatsApp.',
  'inbox.block.whatsappFailed': 'Livinjiwe lapha, kodwa i-WhatsApp ayiyivimbanga inombolo: {{error}}',
  'inbox.block.unblocked': 'Ukuvinjwa kwekhasimende kususiwe.',
  'inbox.block.failed': 'Ikhasimende alikwazanga ukuvinjwa noma ukususwa ukuvinjwa.',
  'inbox.block.bar': 'Uvimbe leli khasimende. I-bot ayiliphenduli futhi izimpendulo zivaliwe.',
  'inbox.block.since': 'Livinjwe {{time}}',
  'inbox.block.onWhatsapp': 'Livinjwe naku-WhatsApp'
};

export default inbox;
